import { Component } from '@theme/component';

class ProductRecommendations extends Component {
  /**
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName === 'data-error') continue;
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      this.#loadRecommendations();
      break;
    }
  });

  /** @type {Record<string, string>} */
  #cachedRecommendations = {};

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {AbortController | null} */
  #rvAbortController = null;

  /** @type {boolean} */
  #recentlyViewedLoaded = false;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    this.#initTabs();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#rvAbortController?.abort();
  }

  #initTabs() {
    if (!this.hasAttribute('data-has-tabs')) return;

    this.#checkRecentlyViewedEligibility();

    const tablist = this.querySelector('[role="tablist"]');
    if (!tablist) return;

    const tabs = /** @type {HTMLButtonElement[]} */ (
      Array.from(tablist.querySelectorAll('[role="tab"]'))
    );
    const panels = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[data-rec-panel]'))
    );

    /** @param {number} index */
    const activate = (index) => {
      tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });

      if (tabs[index]?.dataset.recTab === 'recently-viewed') {
        this.#loadRecentlyViewed();
      }

      this.#updateArrowStates();
    };

    tablist.addEventListener('click', (e) => {
      const btn = /** @type {HTMLElement} */ (e.target)?.closest('[role="tab"]');
      if (!btn || !tablist.contains(btn)) return;
      const idx = tabs.indexOf(/** @type {HTMLButtonElement} */ (btn));
      if (idx === -1) return;
      activate(idx);
    });

    tablist.addEventListener('keydown', (e) => {
      const current = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      if (current === -1) return;
      let next = current;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        next = Math.min(current + 1, tabs.length - 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        next = Math.max(current - 1, 0);
      } else if (e.key === 'Home') {
        next = 0;
      } else if (e.key === 'End') {
        next = tabs.length - 1;
      } else {
        return;
      }
      e.preventDefault();
      activate(next);
      tabs[next].focus();
    });

    this.#initHeaderArrows();
  }

  /**
   * Returns the active scroller element for the visible panel.
   * @returns {{ scroller: Element, slide: Element } | null}
   */
  #getActiveScroller() {
    const visiblePanel = this.querySelector('[data-rec-panel]:not([hidden])');
    if (!visiblePanel) return null;

    const slideshow = visiblePanel.querySelector(
      'slideshow-component:not(product-card slideshow-component)'
    );
    const scroller = slideshow?.querySelector('slideshow-slides');
    if (!scroller) return null;

    const slide = scroller.querySelector(':scope > slideshow-slide');
    if (!slide) return null;

    return { scroller, slide };
  }

  #updateArrowStates() {
    const prevArrow = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[data-rec-arrow="previous"]')
    );
    const nextArrow = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[data-rec-arrow="next"]')
    );
    if (!prevArrow || !nextArrow) return;

    const active = this.#getActiveScroller();
    if (!active) {
      prevArrow.disabled = true;
      nextArrow.disabled = true;
      return;
    }

    const { scroller } = active;
    const atStart = scroller.scrollLeft <= 1;
    const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;

    prevArrow.disabled = atStart;
    nextArrow.disabled = atEnd;
  }

  #initHeaderArrows() {
    const arrows = /** @type {NodeListOf<HTMLButtonElement>} */ (
      this.querySelectorAll('[data-rec-arrow]')
    );
    if (!arrows.length) return;

    arrows.forEach((arrow) => {
      arrow.addEventListener('click', () => {
        if (arrow.disabled) return;

        const action = arrow.getAttribute('data-rec-arrow');
        const active = this.#getActiveScroller();
        if (!active) return;

        const { scroller, slide } = active;
        const slideWidth = slide.getBoundingClientRect().width;
        const gap = parseFloat(getComputedStyle(scroller).gap) || 0;

        scroller.scrollBy({
          left: action === 'next' ? slideWidth + gap : -(slideWidth + gap),
          behavior: 'smooth',
        });

        setTimeout(() => this.#updateArrowStates(), 400);
      });
    });

    setTimeout(() => this.#updateArrowStates(), 100);
  }

  async #loadRecentlyViewed() {
    if (this.#recentlyViewedLoaded) return;

    const container = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-recently-viewed-list]')
    );
    if (!container) return;

    const maxProducts = parseInt(container.dataset.maxProducts || '5', 10);
    const minProducts = parseInt(container.dataset.minProducts || '5', 10);
    const currentProductId = this.dataset.productId;

    const handles = this.#getKlaviyoViewedHandles(currentProductId, maxProducts);

    if (handles.length < minProducts) {
      this.#hideRecentlyViewedTab();
      this.#recentlyViewedLoaded = true;
      return;
    }

    try {
      const sectionEl = this.closest('.shopify-section');
      const sectionId = sectionEl?.id?.replace('shopify-section-', '');
      if (!sectionId) {
        this.#hideRecentlyViewedTab();
        this.#recentlyViewedLoaded = true;
        return;
      }

      this.#rvAbortController?.abort();
      this.#rvAbortController = new AbortController();
      const { signal } = this.#rvAbortController;

      // window.Shopify.routes.root is locale-aware and always ends in "/"
      // (e.g. "/" or "/en/"), so a hardcoded "/products/..." would 404 on
      // localized storefronts. See shopify.dev/docs/api/ajax (Locale-aware URLs).
      const routesRoot = window.Shopify?.routes?.root || '/';
      const cardPromises = handles.map((handle) =>
        fetch(`${routesRoot}products/${handle}?section_id=${sectionId}`, { signal })
          .then((r) => (r.ok ? r.text() : ''))
          .then((html) => {
            if (!html) return '';
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const item = doc.querySelector('.product-card-renderer .resource-list__item');
            return item?.innerHTML || '';
          })
          .catch((err) => {
            if (err.name === 'AbortError') return '';
            return '';
          })
      );

      const cards = (await Promise.all(cardPromises)).filter(Boolean);

      if (signal.aborted) return;

      if (cards.length < minProducts) {
        this.#hideRecentlyViewedTab();
        this.#recentlyViewedLoaded = true;
        return;
      }

      const sourceCarousel = this.querySelector(
        '[data-rec-panel="recommendations"] .resource-list'
      );
      if (!sourceCarousel) {
        this.#hideRecentlyViewedTab();
        this.#recentlyViewedLoaded = true;
        return;
      }

      const clone = /** @type {HTMLElement} */ (sourceCarousel.cloneNode(true));

      clone.querySelectorAll('slideshow-slide').forEach((s) => s.remove());

      const scroller = clone.querySelector('slideshow-slides');
      if (!scroller) {
        this.#hideRecentlyViewedTab();
        this.#recentlyViewedLoaded = true;
        return;
      }

      cards.forEach((cardHTML, i) => {
        const slide = document.createElement('slideshow-slide');
        slide.className = 'resource-list__slide';
        slide.style.cssText = `--slideshow-timeline: --slide-${i};`;
        slide.innerHTML = `<div class="resource-list__item">${cardHTML}</div>`;
        scroller.appendChild(slide);
      });

      const slideshowComp = clone.querySelector('slideshow-component');
      if (slideshowComp) {
        const timeline = cards.map((_, i) => `--slide-${i}`).join(', ');
        slideshowComp.style.setProperty('--slideshow-timeline', timeline);
      }

      container.innerHTML = '';
      container.appendChild(clone);
      this.#recentlyViewedLoaded = true;

      requestAnimationFrame(() => this.#updateArrowStates());
    } catch (err) {
      if (err.name !== 'AbortError') {
        this.#hideRecentlyViewedTab();
      }
      this.#recentlyViewedLoaded = true;
    }
  }

  /**
   * @param {string | undefined} currentProductId
   * @param {number} max
   * @returns {string[]}
   */
  #getKlaviyoViewedHandles(currentProductId, max) {
    try {
      const raw = JSON.parse(localStorage.getItem('__kla_viewed') || '[]');
      return raw
        .map((/** @type {any} */ entry) => entry?.[0])
        .filter(Boolean)
        .filter((/** @type {any} */ item) => String(item.ItemId) !== String(currentProductId))
        .slice(0, max)
        .map((/** @type {any} */ item) => {
          const match = item.Url?.match(/\/products\/([^?#]+)/);
          return match?.[1] || '';
        })
        .filter(Boolean);
    } catch {
      return [];
    }
  }

  #checkRecentlyViewedEligibility() {
    const container = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-recently-viewed-list]')
    );
    if (!container) return;

    const minProducts = parseInt(container.dataset.minProducts || '5', 10);
    const currentProductId = this.dataset.productId;
    const handles = this.#getKlaviyoViewedHandles(currentProductId, minProducts);

    if (handles.length < minProducts) {
      this.#hideRecentlyViewedTab();
    }
  }

  #hideRecentlyViewedTab() {
    const tab = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-rec-tab="recently-viewed"]')
    );
    if (tab) tab.hidden = true;

    const panel = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-rec-panel="recently-viewed"]')
    );
    if (panel) panel.hidden = true;

    const recTab = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[data-rec-tab="recommendations"]')
    );
    if (recTab) {
      recTab.setAttribute('aria-selected', 'true');
      recTab.tabIndex = 0;
    }

    const recPanel = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-rec-panel="recommendations"]')
    );
    if (recPanel) recPanel.hidden = false;
  }

  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          this.#recentlyViewedLoaded = false;
          this.#initTabs();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
